import React, { useState, useEffect } from 'react';
import { useStore } from '@store';
import { observer } from 'mobx-react-lite';
import { OverlayTrigger, Tooltip, Collapse } from 'react-bootstrap';
import DonutChart from 'react-donut-chart';
import { addCommas, removeNonNumeric } from '@helper';
import { DateFilter } from '@components';

const AccountInfo = () => {
    const {
        AccountingStore: {
            creditLine,
            startDate,
            endDate,
            getAccountInfo,
            financingAmount,
            borrowedAmount,
            borrowingAmount,
            collateralMarketValue,
            interestRate,
            rateEfficientDate,
            maintenanceRate,
            contractExpiryDate,
            lendDetailUIList,
            subTotalInterest,
            sumEstimateInterest,
            setOpen,
            updateData,
            reset,
        },
    } = useStore();
    const DonutChartData = [
        {
            label: '統e貸額度',
            value: parseInt(creditLine),
        },
        {
            label: '擔保品融通額度',
            value: parseInt(financingAmount),
        },
        {
            label: '剩餘可借金額',
            value: parseInt(borrowedAmount),
        },
        {
            label: '已借金額',
            value: parseInt(borrowingAmount),
        },
    ];

    const DonutChartBgColor = ['#7A6FBE', '#EE5041', '#FFC600', '#5CD8FF'];
    const repaymentAmountTotal = lendDetailUIList.reduce(
        (preVal, curVal) => preVal + curVal.outstandingLoan,
        0
    );
    const [defaultValue, setDefaultValue] = useState('2');
    useEffect(() => {
        getAccountInfo(startDate, endDate, defaultValue);
    }, [defaultValue, startDate, endDate]);
    useEffect(() => {
        return () => {
            reset({ startDate: '', endDate: '' });
        };
    }, []);
    return (
        <div>
            <div className="acbox row mb-3 mb-sm-5">
                <div className="ac-doughnut card mb-0 flex-row">
                    <DonutChart
                        className="card-body col-7 donut"
                        data={DonutChartData}
                        colors={DonutChartBgColor}
                        strokeColor="#FFF"
                        height="200"
                        width="200"
                        formatValues={values => {
                            return `$ ${addCommas(removeNonNumeric(values))}`;
                        }}
                        legend={false}
                    />
                    <ul className="d-sm-flex flex-column fs-12 justify-content-center d-none">
                        <li className="mb-1">
                            <i className="bi bi-circle-fill text-purple me-1" />
                            統e貸額度
                        </li>
                        <li className="mb-1">
                            <i className="bi bi-circle-fill text-pink me-1" />
                            擔保品融通額度
                            <OverlayTrigger
                                placement="top"
                                overlay={
                                    <Tooltip>前一營業日收盤價計算</Tooltip>
                                }
                            >
                                <i className="icon_point bi-exclamation-circle" />
                            </OverlayTrigger>
                        </li>
                        <li className="mb-1">
                            <i className="bi bi-circle-fill text-yellow me-1" />
                            剩餘可借金額
                        </li>
                        <li className="mb-1">
                            <i className="bi bi-circle-fill text-blue me-1" />
                            已借金額
                        </li>
                    </ul>
                </div>
                <div className="ac-data flex-grow-1 flex-shrink-1">
                    <div className="card h-100 mb-0">
                        <div className="acc-info card-body text-center px-0 px-md-3 pb-sm-0">
                            <div className="row row-cols-3 g-0">
                                <div className="col">
                                    <div className="p-2">
                                        <h5 className="price text-black fw-bolder">
                                            <span>$</span>
                                            {addCommas(
                                                removeNonNumeric(
                                                    collateralMarketValue
                                                )
                                            )}
                                        </h5>
                                        <small>
                                            擔保品市值
                                            <OverlayTrigger
                                                placement="top"
                                                overlay={
                                                    <Tooltip>
                                                        前一營業日收盤價計算
                                                    </Tooltip>
                                                }
                                            >
                                                <i className="icon_point bi-exclamation-circle" />
                                            </OverlayTrigger>
                                        </small>
                                    </div>
                                </div>
                                <div className="col">
                                    <div className="p-2">
                                        <h5 className="price text-black fw-bolder">
                                            <span>$</span>
                                            {addCommas(
                                                removeNonNumeric(
                                                    sumEstimateInterest
                                                )
                                            )}
                                        </h5>
                                        <small>預估應繳利息</small>
                                    </div>
                                </div>
                                <div className="col">
                                    <div className="p-2">
                                        <h5 className="price text-black fw-bolder">
                                            {interestRate}
                                            <span>%</span>
                                        </h5>
                                        <small>
                                            適用利率
                                            <OverlayTrigger
                                                placement="top"
                                                overlay={
                                                    <Tooltip>
                                                        本公司於每季依擔保品及您的信用狀況進行利率審查評估
                                                    </Tooltip>
                                                }
                                            >
                                                <i className="icon_point bi-exclamation-circle" />
                                            </OverlayTrigger>
                                        </small>
                                    </div>
                                </div>
                            </div>
                            <div className="row row-cols-3 g-0 pt-1">
                                <div className="col">
                                    <div className="p-2">
                                        <h5 className="price text-black fw-bolder">
                                            {rateEfficientDate}
                                        </h5>
                                        <small>利率生效日</small>
                                    </div>
                                </div>
                                <div className="col">
                                    <div className="p-2">
                                        <h5 className="price text-black fw-bolder">
                                            {maintenanceRate}
                                            <span>%</span>
                                        </h5>
                                        <small>
                                            維持率
                                            <OverlayTrigger
                                                placement="top"
                                                overlay={
                                                    <Tooltip>
                                                        前一營業日收盤價計算
                                                    </Tooltip>
                                                }
                                            >
                                                <i className="icon_point bi-exclamation-circle" />
                                            </OverlayTrigger>
                                        </small>
                                    </div>
                                </div>
                                <div className="col">
                                    <div className="p-2">
                                        <h5 className="price text-black fw-bolder">
                                            {contractExpiryDate}
                                        </h5>
                                        <small>
                                            統e貸開戶日
                                            {/* <OverlayTrigger
                                                placement="top"
                                                overlay={
                                                    <Tooltip>
                                                        初次簽約有效期間三年，自動延展一年
                                                    </Tooltip>
                                                }
                                            >
                                                <i className="icon_point bi-exclamation-circle" />
                                            </OverlayTrigger> */}
                                        </small>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <h6 className="title fs-22 fw-bolder mb-3">已借款明細</h6>

            <div className="filter-info d-md-flex flex-nowrap justify-content-md-between align-items-center mb-2">
                <DateFilter
                    defaultValue={defaultValue}
                    data={lendDetailUIList}
                    setDefaultValue={setDefaultValue}
                    updateData={updateData}
                />
                <ul className="fs-14 d-flex flex-nowrap justify-content-md-between align-items-sm-end m-1 m-sm-0">
                    <li className="me-1">
                        <small>未還金額：</small>
                        <span className="red">
                            ${addCommas(removeNonNumeric(repaymentAmountTotal))}
                        </span>
                    </li>
                    <li>
                        <small>預估利息：</small>
                        <span className="red">
                            $ {addCommas(removeNonNumeric(subTotalInterest))}
                        </span>
                    </li>
                </ul>
            </div>

            <div className="list-collapse mo_pbm">
                <div className="list-thead text-white fs-6 d-none d-md-block">
                    <div className="row text-wrap">
                        <div className="col-sm-1 col-md-2 text-center">
                            借款日期
                        </div>
                        <div className="col-sm-3 text-center">借款序號</div>
                        <div className="col-sm-3 col-md-2 text-end">
                            未還金額/借貸手續費
                        </div>
                        <div className="col-sm-2 text-end">起息日/預估利息</div>
                        <div className="col-sm-2 text-center">到期日</div>
                        <div className="col-sm-1 text-center">詳細</div>
                    </div>
                </div>

                {lendDetailUIList.map(
                    (
                        {
                            applyNo,
                            applyDate,
                            outstandingLoan,
                            fee,
                            rateEfficientDate,
                            deadLine,
                            estimateInterest,
                            extendedAnn,
                            repayDetailUIList,
                            open,
                        },
                        index
                    ) => {
                        return (
                            <div
                                className={`coll-goup position-relative mb-2 mb-md-1 ${
                                    open ? 'active' : ''
                                }`}
                                key={`lendUI ${index}`}
                            >
                                <div className="list-thead  text-white fs-12 d-md-none d-block ps-2">
                                    <span className="pe-3">
                                        借款日期
                                        <span> {applyDate}</span>
                                    </span>
                                    <span className="d-block">
                                        借款序號 <span> {applyNo}</span>
                                    </span>
                                </div>

                                <div
                                    className={`list-body text-dark fs-6 ${
                                        !open ? 'collapsed' : ''
                                    }`}
                                >
                                    <div
                                        className={`row gx-md-4 gx-2 px-3 px-sm-0  ${
                                            open ? 'text-white' : ''
                                        }`}
                                    >
                                        <div className="col-sm-2 text-center d-none d-md-block">
                                            {applyDate}
                                        </div>
                                        <div className="col-sm-3 text-center d-none d-md-block">
                                            {applyNo}
                                        </div>
                                        <div className="row col-md-2 g-0 text-end pe-md-4">
                                            <p className="col-6 col-md-12 text-md-end text-start ps-2">
                                                <small className="d-md-none">
                                                    未還金額
                                                </small>
                                                <span className="d-inline-block">
                                                    $
                                                    {addCommas(
                                                        removeNonNumeric(
                                                            outstandingLoan
                                                        )
                                                    )}
                                                </span>
                                            </p>
                                            <p
                                                className={`col-6 col-md-12 pe-2 ${
                                                    open
                                                        ? 'text-white'
                                                        : 'text-black-50'
                                                }`}
                                            >
                                                <small className="d-md-none">
                                                    借貸手續費
                                                </small>
                                                <span className="d-inline-block">
                                                    $
                                                    {addCommas(
                                                        removeNonNumeric(fee)
                                                    )}
                                                </span>
                                            </p>
                                        </div>
                                        <div className="row col-md-2 gx-md-2 g-0 text-end">
                                            <div className="col-6 col-md-12 text-md-end text-start ps-2">
                                                <small className="d-md-none">
                                                    起息日
                                                </small>
                                                <span className="d-inline-block">
                                                    {rateEfficientDate}
                                                </span>
                                            </div>
                                            <p
                                                className={`col-6 col-md-12 pe-2  ${
                                                    open
                                                        ? 'text-white'
                                                        : 'text-black-50'
                                                }`}
                                            >
                                                <small className="d-md-none">
                                                    預估利息
                                                </small>
                                                <span className="d-inline-block">
                                                    $
                                                    {addCommas(
                                                        removeNonNumeric(
                                                            estimateInterest
                                                        )
                                                    )}
                                                </span>
                                            </p>
                                        </div>
                                        <div className="row col-md-2 gx-md-2 g-0 text-center">
                                            <div className="col-6 col-md-12 text-md-center text-start ps-2">
                                                <small className="d-md-none">
                                                    到期日
                                                </small>
                                                <span className="d-inline-block">
                                                    {deadLine}
                                                </span>
                                            </div>
                                            {/* <p
                                                className={`col-6 col-md-12 pe-2  ${
                                                    open
                                                        ? 'text-white'
                                                        : 'text-black-50'
                                                }`}
                                            >
                                                <small className="d-md-none">
                                                    展延註記
                                                </small>
                                                <span className="d-inline-block">
                                                    {extendedAnn}
                                                </span>
                                            </p> */}
                                        </div>
                                        <div className="icon-down col-sm-1 d-flex align-items-center justify-content-end">
                                            <i
                                                className="bi bi-chevron-down red fs-5 cur-pointer"
                                                onClick={() => {
                                                    setOpen({
                                                        index,
                                                        value: !open,
                                                    });
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <Collapse in={open}>
                                    <div className="p-1">
                                        <div className="scrol_sty overflow-auto">
                                            <table
                                                cellSpacing={0}
                                                className="table table_sty2 bg-white mb-0"
                                            >
                                                <thead>
                                                    <tr>
                                                        <th className="fs-15 text-center fw-normal text-nowrap">
                                                            還款申請日期
                                                        </th>
                                                        <th className="fs-15 text-center fw-normal text-nowrap">
                                                            止息日期
                                                        </th>
                                                        <th className="fs-15 text-center fw-normal text-nowrap">
                                                            還款序號
                                                        </th>
                                                        <th className="fs-15 text-center fw-normal text-nowrap">
                                                            還款種類
                                                        </th>
                                                        <th className="fs-15 text-end fw-normal text-nowrap">
                                                            <div className="pe-sm-4">
                                                                還款金額
                                                            </div>
                                                        </th>
                                                        <th className="fs-15 text-end fw-normal text-nowrap">
                                                            <div className="pe-sm-4">
                                                                利息
                                                            </div>
                                                        </th>
                                                        <th className="fs-15 text-end fw-normal text-nowrap">
                                                            <div className="pe-sm-4">
                                                                淨收付金額
                                                            </div>
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody className="bd-1">
                                                    {repayDetailUIList.map(
                                                        (
                                                            {
                                                                repayMentDate,
                                                                restDate,
                                                                applicationNumber,
                                                                repayMentAccountType,
                                                                repayMentAmount,
                                                                repayMentInterest,
                                                                netPayAmount,
                                                            },
                                                            index
                                                        ) => {
                                                            return (
                                                                <tr
                                                                    key={`repayDetailUIs ${index}`}
                                                                >
                                                                    <th className="text-center fw-normal">
                                                                        {
                                                                            repayMentDate
                                                                        }
                                                                    </th>
                                                                    <td className="text-center text-nowrap">
                                                                        {
                                                                            restDate
                                                                        }
                                                                    </td>
                                                                    <td className="text-center">
                                                                        {
                                                                            applicationNumber
                                                                        }
                                                                    </td>
                                                                    <td className="text-center">
                                                                        <span className="border border-info rounded text-info fs-15">
                                                                            {repayMentAccountType ===
                                                                            's'
                                                                                ? '賣 股'
                                                                                : '現 金'}
                                                                        </span>
                                                                    </td>
                                                                    <td>
                                                                        <div className="pe-sm-4 text-end">
                                                                            <span>
                                                                                $
                                                                            </span>
                                                                            {addCommas(
                                                                                removeNonNumeric(
                                                                                    repayMentAmount
                                                                                )
                                                                            )}
                                                                        </div>
                                                                    </td>
                                                                    <td>
                                                                        <div className="pe-sm-4 text-end">
                                                                            <span>
                                                                                $
                                                                            </span>
                                                                            {addCommas(
                                                                                removeNonNumeric(
                                                                                    repayMentInterest
                                                                                )
                                                                            )}
                                                                        </div>
                                                                    </td>
                                                                    <td>
                                                                        <div className="pe-sm-4 text-end">
                                                                            <span>
                                                                                -$
                                                                            </span>
                                                                            {addCommas(
                                                                                removeNonNumeric(
                                                                                    netPayAmount
                                                                                )
                                                                            )}
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        }
                                                    )}
                                                    <tr>
                                                        <th className="text-center">
                                                            合計
                                                        </th>
                                                        <td />
                                                        <td />
                                                        <td />
                                                        <td>
                                                            <div className="pe-sm-4 text-end">
                                                                <span>$</span>
                                                                {addCommas(
                                                                    removeNonNumeric(
                                                                        repayDetailUIList.reduce(
                                                                            (
                                                                                preVal,
                                                                                curVal
                                                                            ) =>
                                                                                preVal +
                                                                                curVal.repayMentAmount,
                                                                            0
                                                                        )
                                                                    )
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="pe-sm-4 text-end">
                                                                <span>$</span>
                                                                {addCommas(
                                                                    removeNonNumeric(
                                                                        repayDetailUIList.reduce(
                                                                            (
                                                                                preVal,
                                                                                curVal
                                                                            ) =>
                                                                                preVal +
                                                                                curVal.repayMentInterest,
                                                                            0
                                                                        )
                                                                    )
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="pe-sm-4 text-end">
                                                                <span>-$</span>
                                                                {addCommas(
                                                                    removeNonNumeric(
                                                                        repayDetailUIList.reduce(
                                                                            (
                                                                                preVal,
                                                                                curVal
                                                                            ) =>
                                                                                preVal +
                                                                                curVal.netPayAmount,
                                                                            0
                                                                        )
                                                                    )
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </Collapse>
                            </div>
                        );
                    }
                )}
            </div>
            <p className="fs-14 text-end mt-2">
                <label className="text-black2 align-text-bottom ">合計</label>
                <b className="red ps-3 fs-5">
                    <span>$</span>
                    {addCommas(
                        removeNonNumeric(
                            repaymentAmountTotal + subTotalInterest
                        )
                    )}
                </b>
            </p>
        </div>
    );
};

export default observer(AccountInfo);
